import React, { useContext, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  AssessmentRounded as PaymentReportIcon,
  AssignmentRounded as ReceiptReportIcon,
  TaskAltRounded as SettlementReportIcon,
  Inventory2Rounded as StockReportIcon,
  LayersRounded as FloorReportIcon,
  InsightsRounded as BranchReportIcon,
} from "@mui/icons-material";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import { canViewBranchReport, toRoleId } from "../../utils/roles";
import PaymentReport from "./PaymentReport";
import ReceiptReport from "./ReceiptReport";
import SettlementReport from "./SettlementReport";
import StockCheckScreen from "./StockCheckScreen";
import FloorReport from "./FloorReport";
import BranchReport from "../BranchReport";
import { reportFixedSx, reportSwitcherStripSx } from "./reportLayout";
import PawnNavButtons from "./pawnNavButtons";

// ─── Blue/White Design Tokens (shared across the pawn report screens) ────────
const BLUE = "#2563EB";
const INK = "#0F172A";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const BG_ALT = "#F1F5F9";

// The reports, keyed by the `?type=` query param. Each entry carries the
// heading shown at the top of the unified screen and the body component that
// renders in embedded mode (its own header hidden — this screen supplies it).
//
// An optional `visible(session)` hides an entry from people who may not open
// it. Only the branch report uses one; everything else here is the day's work,
// which everybody at the counter does.
const REPORTS = {
  payment: {
    label: "Payment",
    title: "Payment Report",
    icon: <PaymentReportIcon />,
    Component: PaymentReport,
  },
  receipt: {
    label: "Receipt",
    title: "Receipt Report",
    icon: <ReceiptReportIcon />,
    Component: ReceiptReport,
  },
  settlement: {
    label: "Settlement",
    title: "Settlement Report",
    icon: <SettlementReportIcon />,
    Component: SettlementReport,
  },
  // Stock is the FULL Stock Check screen (scan form + scanned grid + save/reset
  // + its own embedded register), not just a filtered register — so it renders
  // the whole StockCheckScreen, only with its header suppressed via `embedded`.
  stock: {
    label: "Stock",
    title: "Stock Report",
    icon: <StockReportIcon />,
    Component: StockCheckScreen,
  },
  // Floorwise strong-room summary: every floor of the store with the loans
  // lying on it as on a date (ticket count + jewel / tare / total weights).
  floor: {
    label: "Floor",
    title: "Floor Report",
    icon: <FloorReportIcon />,
    Component: FloorReport,
  },
  // Every shop of the store side by side. It used to hang off the Daily Tick
  // sidebar only, which meant an owner of a pawn store had to leave the module
  // to answer "which branch is carrying the book" — so for a pawn store it now
  // lives here instead, and the rail entry is not drawn at all (see Sidebar's
  // isVisibleForStore).
  //
  // OWNER'S EYES ONLY, and hidden rather than disabled: the report names what
  // every other branch took in, which is not a manager's business, let alone
  // the counter's. Two conditions, both answered from the session so gating
  // costs no request — the role must be the owner's, and the store must have
  // more than one branch (on a single-branch store the report is one row and a
  // total that repeats it). BranchReport re-checks the role itself, so a typed
  // ?type=branch does not get in either.
  //
  // THE BRANCH COUNT IS READ FROM THE STORE ROW FIRST, not from
  // canViewAllBranches. That flag says the same thing — the server computes it
  // as "head-or-service role AND more than one branch" — but it is one field
  // that has to survive being passed through three login paths, and the OTP
  // path used to drop it, which stored a hard false and left an owner with no
  // branch chip until they happened to switch branch. The store's own branch
  // list arrives in the same payload and cannot go missing without the branch
  // switcher breaking too, so it is the primary; the flag stays as a fallback
  // for a session cached before the store row carried counts.
  branch: {
    label: "Branch",
    title: "Branch Report",
    icon: <BranchReportIcon />,
    Component: BranchReport,
    visible: ({ roleId, branchCount, canViewAllBranches }) =>
      canViewBranchReport(roleId) &&
      (branchCount > 1 || Boolean(canViewAllBranches)),
  },
};

// The Day Book chip used to live here. It moved to the Ledger Reports screen as
// the Cash Book (/pawn/accounts-ledger?type=cashbook), which reads the same
// register off the same ledger rows, so keeping both was two doors onto one
// report. A stale `?type=daybook` link falls through to DEFAULT_TYPE below.

const DEFAULT_TYPE = "payment";

const PawnReports = () => {
  const { currentStoreInfo, userInfo, role, canViewAllBranches } =
    useContext(AutoTaskAuthContext) || {};
  const [searchParams, setSearchParams] = useSearchParams();

  // Which reports this person gets. Recomputed rather than frozen at mount
  // because the session hydrates from storage a beat after the first render —
  // a chip that is allowed appears as soon as the role lands.
  const visibleReports = useMemo(() => {
    const session = {
      roleId: toRoleId(role ?? userInfo?.role),
      // branch_count is what buildSessionPayload flattens onto the store row;
      // the list it was counted from is beside it, so either answers.
      branchCount: Number(
        currentStoreInfo?.branch_count ??
          currentStoreInfo?.branches?.length ??
          0,
      ),
      canViewAllBranches,
    };
    return Object.entries(REPORTS).filter(
      ([, r]) => !r.visible || r.visible(session),
    );
  }, [
    role,
    userInfo?.role,
    canViewAllBranches,
    currentStoreInfo?.branch_count,
    currentStoreInfo?.branches,
  ]);

  // The active report comes from the URL (?type=), so the old per-report routes
  // can redirect straight to the right tab and links stay shareable. A type
  // this person may not see falls back to the default exactly as an unknown one
  // does — without rewriting the URL, so a head who deep-links to ?type=branch
  // lands on it once the session has hydrated.
  const rawType = searchParams.get("type");
  const type = visibleReports.some(([key]) => key === rawType)
    ? rawType
    : DEFAULT_TYPE;
  const active = REPORTS[type];
  const ActiveReport = active.Component;

  const handleChange = (_e, next) => {
    if (!next || next === type) return;
    // replace: switching reports shouldn't stack browser-history entries.
    setSearchParams({ type: next }, { replace: true });
  };

  const chips = useMemo(
    () => (
      <ToggleButtonGroup
        value={type}
        exclusive
        size="small"
        onChange={handleChange}
        sx={{
          // Never `100%`: the chips can't share a phone's width without either
          // shrinking below their own text or widening the page, so the group
          // keeps its natural width and the strip around it scrolls instead.
          width: "auto",
          "& .MuiToggleButton-root": {
            textTransform: "none",
            px: { xs: 1, sm: 1.5, md: 2 },
            py: { xs: 0.6, md: 0.75 },
            fontSize: { xs: "0.72rem", sm: "0.8rem" },
            fontWeight: 700,
            color: INK_MUTED,
            borderColor: BORDER,
            gap: 0.5,
          },
          "& .Mui-selected": {
            bgcolor: `${BLUE} !important`,
            color: "#fff !important",
          },
        }}
      >
        {visibleReports.map(([key, r]) => (
          <ToggleButton key={key} value={key}>
            {React.cloneElement(r.icon, {
              sx: { fontSize: { xs: 16, md: 18 }, mr: 0.5 },
            })}
            {r.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type, visibleReports],
  );

  return (
    <Box
      sx={{
        // Flex column so the active report can flex into the leftover height and
        // keep the page itself scroll-free (see reportLayout.js).
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        // Horizontal is pinned shut deliberately: the switcher strip and every
        // report table scroll sideways inside themselves, so a page-level
        // horizontal scroll only ever meant the heading and filters had been
        // dragged off the left edge.
        overflowY: "auto",
        overflowX: "hidden",
        bgcolor: BG_ALT,
        p: { xs: 1.25, sm: 1.5, md: 2.5 },
      }}
    >
      {/* ── Top bar: heading + report switcher. On desktop the switcher sits at
          the right edge; on mobile it stacks ABOVE the heading. ── */}
      <Box
        sx={{
          display: "flex",
          // Stacks until there is genuinely room for a heading AND the chips on
          // one line — which is only true from lg up, not md.
          flexDirection: { xs: "column", lg: "row" },
          alignItems: { xs: "stretch", lg: "center" },
          justifyContent: "space-between",
          gap: { xs: 1, sm: 1.5 },
          mb: { xs: 1.5, md: 2 },
          minWidth: 0,
          ...reportFixedSx,
        }}
      >
        {/* Heading — order 2 on mobile (below the chips), left on desktop. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 1.5 },
            order: { xs: 2, lg: 1 },
            minWidth: 0,
          }}
        >
          <PawnNavButtons />
          <Box
            sx={{
              width: { xs: 36, sm: 42 },
              height: { xs: 36, sm: 42 },
              flexShrink: 0,
              borderRadius: 2,
              bgcolor: BLUE,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
            }}
          >
            {active.icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.05rem", sm: "1.25rem" },
                color: INK,
              }}
            >
              {active.title}
            </Typography>
            <Typography
              noWrap
              sx={{ fontSize: "0.75rem", color: INK_MUTED }}
            >
              {currentStoreInfo?.name ? `${currentStoreInfo.name}` : ""}
            </Typography>
          </Box>
        </Box>

        {/* Switcher — order 1 on mobile (top), right edge on desktop. The strip
            scrolls sideways so the chips never widen the page. */}
        <Box sx={{ order: { xs: 1, lg: 2 }, ...reportSwitcherStripSx }}>
          {chips}
        </Box>
      </Box>

      {/* Active report body (header suppressed via `embedded`). Only the
          selected report is mounted, so just it fetches. The Stock report is the
          full Stock Check screen (scan form + grid + its own register). */}
      <ActiveReport embedded />
    </Box>
  );
};

export default PawnReports;
